/**
 * Settings > Campaigns — catalog sync (Worker), filter visibility, Status sidebar icons.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Tooltip } from '../../components/Tooltip';
import { getStatusIcon, STATUS_ICON_OPTIONS } from '../../lib/statusIcons';
import {
  isCampaignFilterVisible,
  normCampaignGroupKey,
  displayGroupName,
  type CampaignFilterVisibilityConfig,
  type CampaignOverride,
} from '../../lib/campaignFilterVisibility';

type CatalogRow = {
  campaign_id: number;
  campaign_title: string | null;
  campaign_description: string | null;
  campaign_type: string | null;
  campaign_group_name: string | null;
  is_running: number;
  synced_at: string | null;
};

function cloneVisibility(c: CampaignFilterVisibilityConfig | null | undefined): CampaignFilterVisibilityConfig {
  return {
    groups: { ...(c?.groups ?? {}) },
    campaigns: { ...(c?.campaigns ?? {}) },
  };
}

export default function SettingsCampaigns() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<string | null>(null);
  const [rows, setRows] = useState<CatalogRow[]>([]);
  const [presenceWsUrl, setPresenceWsUrl] = useState('');
  const [visibilityDraft, setVisibilityDraft] = useState<CampaignFilterVisibilityConfig>(() => cloneVisibility(null));
  const [visibilityBaseline, setVisibilityBaseline] = useState<CampaignFilterVisibilityConfig>(() => cloneVisibility(null));
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [campaignIcons, setCampaignIcons] = useState<Record<string, string>>({});
  const [teamIcons, setTeamIcons] = useState<Record<string, string>>({});
  const [iconSaving, setIconSaving] = useState(false);

  const workerOrigin = (() => {
    const ws = presenceWsUrl.trim();
    if (!ws.startsWith('ws')) return '';
    try {
      const normalized = ws.startsWith('wss://')
        ? `https://${ws.slice('wss://'.length)}`
        : `http://${ws.slice('ws://'.length)}`;
      return new URL(normalized).origin;
    } catch {
      return '';
    }
  })();

  const loadCatalog = useCallback(async () => {
    if (!workerOrigin) throw new Error('Set presence_ws_url in Integrations first.');
    const res = await fetch(`${workerOrigin}/campaigns/catalog`);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error((d as { error?: string }).error || `HTTP ${res.status}`);
    }
    const data = (await res.json()) as { campaigns?: CatalogRow[] };
    setRows(Array.isArray(data.campaigns) ? data.campaigns : []);
  }, [workerOrigin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const base = typeof window !== 'undefined' ? window.location.origin : '';
        const cfgRes = await fetch(`${base}/api/config?keys=presence_ws_url,campaign_filter_visibility,status_icons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          if (typeof cfg.presence_ws_url === 'string' && !cancelled) setPresenceWsUrl(cfg.presence_ws_url);
          const raw = cfg.campaign_filter_visibility;
          let vis: CampaignFilterVisibilityConfig | null = null;
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) vis = raw as CampaignFilterVisibilityConfig;
          const next = cloneVisibility(vis);
          if (!cancelled) {
            setVisibilityDraft(next);
            setVisibilityBaseline(cloneVisibility(vis));
          }
          const icons = (cfg.status_icons || {}) as { campaigns?: Record<string, string>; teams?: Record<string, string> };
          if (!cancelled) {
            setCampaignIcons({ ...(icons.campaigns ?? {}) });
            setTeamIcons({ ...(icons.teams ?? {}) });
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerOrigin) return;
      try {
        await loadCatalog();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin, loadCatalog]);

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogRow[]>();
    for (const r of rows) {
      const g = normCampaignGroupKey(r.campaign_group_name);
      const list = map.get(g) ?? [];
      list.push(r);
      map.set(g, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => (a.campaign_title ?? '').localeCompare(b.campaign_title ?? '', undefined, { sensitivity: 'base' }));
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === '__ungrouped__') return 1;
      if (b === '__ungrouped__') return -1;
      return displayGroupName(a).localeCompare(displayGroupName(b), undefined, { sensitivity: 'base' });
    });
    return { map, keys };
  }, [rows]);

  const visibleCount = useMemo(() => {
    return rows.filter((r) => isCampaignFilterVisible(r.campaign_id, r.campaign_group_name, visibilityDraft)).length;
  }, [rows, visibilityDraft]);

  const visibilityDirty = useMemo(() => {
    return JSON.stringify(visibilityDraft) !== JSON.stringify(visibilityBaseline);
  }, [visibilityDraft, visibilityBaseline]);

  const setGroupEnabled = useCallback((groupKey: string, enabled: boolean) => {
    setVisibilityDraft((d) => {
      const next = cloneVisibility(d);
      if (enabled) {
        delete next.groups?.[groupKey];
        if (next.groups && Object.keys(next.groups).length === 0) delete next.groups;
      } else {
        next.groups = { ...next.groups, [groupKey]: false };
      }
      return next;
    });
  }, []);

  const setCampaignOverride = useCallback((campaignId: number, value: 'default' | CampaignOverride) => {
    setVisibilityDraft((d) => {
      const next = cloneVisibility(d);
      const idKey = String(campaignId);
      if (!next.campaigns) next.campaigns = {};
      if (value === 'default') {
        delete next.campaigns[idKey];
        if (Object.keys(next.campaigns).length === 0) delete next.campaigns;
      } else {
        next.campaigns = { ...next.campaigns, [idKey]: value };
      }
      return next;
    });
  }, []);

  const saveVisibility = async () => {
    setVisibilitySaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not signed in');
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const body: Record<string, unknown> = {
        campaign_filter_visibility: {
          groups: visibilityDraft.groups && Object.keys(visibilityDraft.groups).length ? visibilityDraft.groups : undefined,
          campaigns:
            visibilityDraft.campaigns && Object.keys(visibilityDraft.campaigns).length ? visibilityDraft.campaigns : undefined,
        },
      };
      const res = await fetch(`${base}/api/config`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error((d as { error?: string }).error || `HTTP ${res.status}`);
      }
      setVisibilityBaseline(cloneVisibility(visibilityDraft));
      setSuccess('Saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const resetVisibility = () => {
    const empty = cloneVisibility(null);
    setVisibilityDraft(empty);
  };

  const toggleExpanded = (g: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };

  const saveCampaignIcon = useCallback(
    async (titleKey: string, iconName: string) => {
      const token = await getToken();
      if (!token) return;
      const nextCampaigns = { ...campaignIcons, [titleKey]: iconName };
      setCampaignIcons(nextCampaigns);
      setIconSaving(true);
      setError(null);
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const res = await fetch(`${base}/api/config`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status_icons: { campaigns: nextCampaigns, teams: teamIcons },
          }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error((d as { error?: string }).error || `HTTP ${res.status}`);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save icon');
      } finally {
        setIconSaving(false);
      }
    },
    [getToken, campaignIcons, teamIcons]
  );

  const handleSync = async () => {
    if (!workerOrigin) {
      setError('Set presence_ws_url in Integrations first.');
      return;
    }
    setSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${workerOrigin}/campaigns/sync`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
      const upserted = (data as { upserted?: number }).upserted ?? 0;
      const removed = (data as { removedFromCatalog?: number }).removedFromCatalog ?? 0;
      setSuccess(`Updated ${upserted} campaign(s)${removed ? `, removed ${removed}` : ''}.`);
      await loadCatalog();
      const token = await getToken();
      if (token) {
        const base = typeof window !== 'undefined' ? window.location.origin : '';
        const cfgRes = await fetch(`${base}/api/config?keys=campaign_filter_visibility,status_icons`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          const raw = cfg.campaign_filter_visibility;
          let vis: CampaignFilterVisibilityConfig | null = null;
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) vis = raw as CampaignFilterVisibilityConfig;
          setVisibilityDraft(cloneVisibility(vis));
          setVisibilityBaseline(cloneVisibility(vis));
          const icons = (cfg.status_icons || {}) as { campaigns?: Record<string, string>; teams?: Record<string, string> };
          setCampaignIcons({ ...(icons.campaigns ?? {}) });
          setTeamIcons({ ...(icons.teams ?? {}) });
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDiagnostics = async () => {
    if (!workerOrigin) {
      setError('Set presence_ws_url in Integrations first.');
      return;
    }
    setDiagnosing(true);
    setError(null);
    setSuccess(null);
    setDiagnostics(null);
    try {
      const res = await fetch(`${workerOrigin}/campaigns/diagnostics`);
      const raw = await res.text();
      let formatted = raw;
      try {
        formatted = JSON.stringify(JSON.parse(raw), null, 2);
      } catch {
        /* keep raw */
      }
      setDiagnostics(formatted);
      if (!res.ok) {
        try {
          const j = JSON.parse(raw) as { error?: string; reason?: string };
          setError(j.error || j.reason || `HTTP ${res.status}`);
        } catch {
          setError(`HTTP ${res.status}`);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Diagnostics failed');
    } finally {
      setDiagnosing(false);
    }
  };

  if (loading) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  }

  const btnStyle = {
    padding: '0.5rem 1rem',
    borderRadius: 6,
    border: 'none',
    color: 'var(--text)',
    cursor: 'pointer' as const,
  };

  const scrollPanelStyle = {
    maxHeight: 'calc(100vh - 260px)',
    minHeight: '280px',
    overflowY: 'auto' as const,
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    padding: '0.5rem',
    background: 'rgba(255,255,255,0.02)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '960px' }}>
      <h2 style={{ margin: 0, fontSize: '1.125rem' }}>Campaigns</h2>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        <Tooltip content="Save sidebar visibility rules (groups and overrides).">
          <button
            type="button"
            onClick={saveVisibility}
            disabled={visibilitySaving || !visibilityDirty}
            style={{
              ...btnStyle,
              background: visibilityDirty ? 'var(--accent)' : 'rgba(255,255,255,0.12)',
              cursor: visibilitySaving || !visibilityDirty ? 'not-allowed' : 'pointer',
              opacity: visibilitySaving || !visibilityDirty ? 0.7 : 1,
            }}
          >
            {visibilitySaving ? 'Saving…' : 'Save visibility'}
          </button>
        </Tooltip>
        <Tooltip content="Clear all visibility rules (every campaign follows default: visible).">
          <button
            type="button"
            onClick={resetVisibility}
            disabled={visibilitySaving}
            style={{
              ...btnStyle,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            Clear rules
          </button>
        </Tooltip>
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--text)' }}>{visibleCount}</strong> / {rows.length} visible in Status
        </span>
        {visibilityDirty && <span style={{ fontSize: '0.75rem', color: '#eab308' }}>Unsaved</span>}
      </div>

      <div style={scrollPanelStyle}>
        {rows.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '0.5rem' }}>
            No campaigns loaded. Use Refresh list below (configure Integrations first).
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {grouped.keys.map((gKey) => {
              const list = grouped.map.get(gKey) ?? [];
              const expanded = expandedGroups.has(gKey);
              const groupOn = visibilityDraft.groups?.[gKey] !== false;
              return (
                <div key={gKey} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, overflow: 'hidden' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.45rem 0.65rem',
                      background: 'var(--widget-bg)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpanded(gKey)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        width: 28,
                        fontSize: '0.7rem',
                      }}
                      aria-expanded={expanded}
                    >
                      {expanded ? '▼' : '▶'}
                    </button>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: 1, minWidth: 0, cursor: 'pointer' }}>
                      <input type="checkbox" checked={groupOn} onChange={(e) => setGroupEnabled(gKey, e.target.checked)} />
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{displayGroupName(gKey)}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>({list.length})</span>
                    </label>
                  </div>
                  {expanded && (
                    <div style={{ padding: '0.35rem 0.5rem 0.5rem 2rem' }}>
                      {list.map((r, idx) => {
                        const idKey = String(r.campaign_id);
                        const ov = visibilityDraft.campaigns?.[idKey];
                        const selectVal = ov === 'show' || ov === 'hide' ? ov : 'default';
                        const title = r.campaign_title?.trim() || `Campaign #${r.campaign_id}`;
                        const titleKey = r.campaign_title?.trim();
                        const effective = isCampaignFilterVisible(r.campaign_id, r.campaign_group_name, visibilityDraft);
                        const iconName = titleKey ? campaignIcons[titleKey] || 'LayoutList' : 'LayoutList';
                        const RowIcon = getStatusIcon(iconName);
                        return (
                          <div
                            key={r.campaign_id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.35rem 0',
                              borderTop: idx === 0 ? 'none' : '1px solid rgba(255,255,255,0.05)',
                              fontSize: '0.8125rem',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Tooltip content="Status menu icon (same options as Settings → Status icons).">
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                <RowIcon size={16} strokeWidth={2} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                                {titleKey ? (
                                  <select
                                    value={iconName}
                                    disabled={iconSaving}
                                    onChange={(e) => saveCampaignIcon(titleKey, e.target.value)}
                                    aria-label={`Icon for ${title}`}
                                    style={{
                                      padding: '0.2rem 0.35rem',
                                      borderRadius: 4,
                                      border: '1px solid rgba(255,255,255,0.15)',
                                      background: 'var(--bg)',
                                      color: 'var(--text)',
                                      fontSize: '0.72rem',
                                      maxWidth: '8.5rem',
                                    }}
                                  >
                                    {STATUS_ICON_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>—</span>
                                )}
                              </span>
                            </Tooltip>
                            <span style={{ flex: 1, minWidth: '10rem' }}>{title}</span>
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>#{r.campaign_id}</span>
                            <select
                              value={selectVal}
                              onChange={(e) => setCampaignOverride(r.campaign_id, e.target.value as 'default' | CampaignOverride)}
                              style={{
                                padding: '0.25rem 0.35rem',
                                borderRadius: 4,
                                border: '1px solid rgba(255,255,255,0.15)',
                                background: 'var(--bg)',
                                color: 'var(--text)',
                                fontSize: '0.75rem',
                              }}
                            >
                              <option value="default">Follow group</option>
                              <option value="show">Always show</option>
                              <option value="hide">Always hide</option>
                            </select>
                            <span
                              style={{
                                fontSize: '0.7rem',
                                padding: '0.15rem 0.35rem',
                                borderRadius: 4,
                                background: effective ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.12)',
                                color: effective ? '#86efac' : '#fca5a5',
                              }}
                            >
                              {effective ? 'Visible' : 'Hidden'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip content="Upsert campaigns from ipSCAPE. Existing visibility and icons stay; entries for removed campaigns are cleaned automatically.">
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: syncing ? 'not-allowed' : 'pointer',
            }}
          >
            {syncing ? 'Refreshing…' : 'Refresh list'}
          </button>
        </Tooltip>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{rows.length} in database</span>
        <Tooltip content="Check Platform API URL and credentials on the Worker.">
          <button
            type="button"
            onClick={handleDiagnostics}
            disabled={diagnosing}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(59,130,246,0.25)',
              border: '1px solid rgba(59,130,246,0.45)',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: diagnosing ? 'not-allowed' : 'pointer',
            }}
          >
            {diagnosing ? '…' : 'Diagnostics'}
          </button>
        </Tooltip>
      </div>

      {success && <p style={{ color: '#22c55e', fontSize: '0.875rem', margin: 0 }}>{success}</p>}
      {error && <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: 0 }}>{error}</p>}
      {diagnostics && (
        <pre
          style={{
            margin: 0,
            padding: '0.75rem',
            fontSize: '0.75rem',
            maxHeight: 200,
            overflow: 'auto',
            background: 'var(--bg)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 6,
            color: 'var(--text-muted)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {diagnostics}
        </pre>
      )}
    </div>
  );
}
